import { TripTransport, Location, TransportLeg } from "@/types";
import { formatInTimeZone } from "date-fns-tz";
import { Car, Bus, Plane, Ship, Train } from "lucide-react";
import { DateTime } from "luxon";
import { useCallback } from "react";

interface TransportCardProps {
  transport: TripTransport | null;
  fromLocation: Location;
  toLocation: Location;
  handleTransportButtonClick: (location: Location) => void;
}

const getTransportIcon = (mode: string) => {
  switch (mode) {
    case "flight":
      return <Plane className="w-4 h-4" />;
    case "train":
      return <Train className="w-4 h-4" />;
    case "bus":
      return <Bus className="w-4 h-4" />;
    case "ferry":
      return <Ship className="w-4 h-4" />;
    default:
      return <Car className="w-4 h-4" />;
  }
};

const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h${mins > 0 ? ` ${mins}m` : ""}`;
};

const TransportCard: React.FC<TransportCardProps> = ({
  transport,
  fromLocation,
  toLocation,
  handleTransportButtonClick,
}) => {
  const renderAirlineAvatars = useCallback(() => {
    if (!transport?.legs) return [];

    const uniqueAirlines = Array.from(
      new Set(transport.legs.map((leg) => leg.provider.code))
    );

    return uniqueAirlines.map((airline, index) => {
      const imageUrl = `https://images.kiwi.com/airlines/128x128/${airline}.png?default=airline.png`;
      return (
        <div
          key={airline}
          className="airline-avatar rounded-full border-2 border-white shadow-sm bg-white"
          style={{
            width: "2.5rem",
            height: "2.5rem",
            backgroundImage: `url(${imageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            marginLeft: index > 0 ? "-1rem" : "0",
            zIndex: uniqueAirlines.length - index,
          }}
        />
      );
    });
  }, [transport?.legs]);

  const renderAirlineNames = useCallback(() => {
    if (!transport?.legs) return "";

    const uniqueAirlines = new Set(
      transport.legs.map((leg) => leg.provider.name)
    );
    return uniqueAirlines.size > 1
      ? "Multiple Airlines"
      : Array.from(uniqueAirlines).join(" + ");
  }, [transport?.legs]);

  if (!transport || !transport.legs || transport.legs.length === 0) {
    return (
      <div
        className="relative my-8 cursor-pointer group"
        onClick={() => handleTransportButtonClick(toLocation)}
      >
        {/* Top connector */}
        <div className="absolute left-1/2 -top-5 flex flex-col items-center justify-evenly space-y-1">
          <div className="w-1 h-1 rounded-full bg-neutral-300"></div>
          <div className="w-1 h-1 rounded-full bg-neutral-300"></div>
        </div>

        <div className="bg-white rounded-lg p-4 border border-neutral-200 hover:border-indigo-300 hover:bg-indigo-50/50 shadow-sm transition-all duration-200">
          <div className="flex items-center justify-center gap-2">
            <Plane className="w-4 h-4 text-neutral-500 group-hover:text-indigo-600 transition-colors duration-200" />
            <span className="text-sm text-neutral-600 group-hover:text-indigo-700 font-medium transition-colors duration-200">
              Add transport
            </span>
          </div>
        </div>

        {/* Bottom connector */}
        <div className="absolute left-1/2 -bottom-5 flex flex-col items-center justify-evenly space-y-1">
          <div className="w-1 h-1 rounded-full bg-neutral-300"></div>
          <div className="w-1 h-1 rounded-full bg-neutral-300"></div>
        </div>
      </div>
    );
  }

  // Get the first leg for basic info
  const firstLeg = transport.legs[0];
  const lastLeg = transport.legs[transport.legs.length - 1];

  // Safety check for leg data
  if (!firstLeg || !lastLeg || !firstLeg.departure || !lastLeg.arrival) {
    return (
      <div className="relative my-8">
        {/* Top connector */}
        <div className="absolute left-1/2 -top-5 flex flex-col items-center justify-evenly space-y-1">
          <div className="w-1 h-1 rounded-full bg-neutral-300"></div>
          <div className="w-1 h-1 rounded-full bg-neutral-300"></div>
        </div>

        <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-100">
          <div className="flex items-center justify-center gap-2">
            <Plane className="w-4 h-4 text-neutral-400" />
            <span className="text-sm text-neutral-500">
              Incomplete transport data
            </span>
          </div>
        </div>

        {/* Bottom connector */}
        <div className="absolute left-1/2 -bottom-5 flex flex-col items-center justify-evenly space-y-1">
          <div className="w-1 h-1 rounded-full bg-neutral-300"></div>
          <div className="w-1 h-1 rounded-full bg-neutral-300"></div>
        </div>
      </div>
    );
  }

  // Calculate total duration across all legs
  const totalDuration = transport.legs.reduce(
    (total, leg) => total + (leg.duration || 0),
    0
  );

  // Format departure and arrival times
  const departureTime = DateTime.fromISO(firstLeg.departure.time.utc)
    .setZone(fromLocation.date.time_zone)
    .toFormat("h:mm a");

  const arrivalTime = DateTime.fromISO(lastLeg.arrival.time.utc)
    .setZone(toLocation.date.time_zone)
    .toFormat("h:mm a");

  // Format departure date
  const departureDate = DateTime.fromISO(firstLeg.departure.time.utc)
    .setZone(fromLocation.date.time_zone)
    .toFormat("d MMM");

  // Check if multi-leg journey
  const isMultiLeg = transport.legs.length > 1;

  return (
    <div className="relative my-8">
      {/* Top connector */}
      <div className="absolute left-1/2 -top-5 flex flex-col items-center justify-evenly space-y-1">
        <div className="w-1 h-1 rounded-full bg-neutral-300"></div>
        <div className="w-1 h-1 rounded-full bg-neutral-300"></div>
      </div>
      <div className="bg-white rounded-xl border border-neutral-100 overflow-hidden">
        {/* Minimal header */}
        <div className="bg-neutral-100 px-3 py-2 text-neutral-500 flex justify-between items-center text-xs">
          <div className="flex items-center gap-2">
            {renderAirlineAvatars()}
            <span className="text-xs">{renderAirlineNames()}</span>
          </div>
          <span className="text-xs">{departureDate}</span>
        </div>

        {/* Main content */}
        <div className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex flex-col items-start space-y-2">
              <span className="text-sm font-medium">{departureTime}</span>
              <span className="text-xs text-neutral-500">
                {fromLocation.name}
              </span>
            </div>

            <div className="flex-1 mx-8 flex flex-col items-center space-y-1">
              <div className="text-xs text-neutral-500">
                {formatDuration(totalDuration)}
              </div>
              <div className="w-full flex items-center">
                <div className="h-[1px] flex-1 bg-neutral-200"></div>
                <div className="mx-8 text-neutral-500">
                  {getTransportIcon(firstLeg.mode)}
                </div>
                <div className="h-[1px] flex-1 bg-neutral-200"></div>
              </div>
              {isMultiLeg && (
                <div className="text-xs text-indigo-500">
                  {transport.legs.length} stops
                </div>
              )}
            </div>

            <div className="flex flex-col items-end space-y-1">
              <span className="text-sm font-medium">{arrivalTime}</span>
              <span className="text-xs text-neutral-500">
                {toLocation.name}
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Bottom connector */}
      <div className="absolute left-1/2 -bottom-5 flex flex-col items-center justify-evenly space-y-1">
        <div className="w-1 h-1 rounded-full bg-neutral-300"></div>
        <div className="w-1 h-1 rounded-full bg-neutral-300"></div>
      </div>
    </div>
  );
};

export default TransportCard;
